import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Logo from "../assets/logo.png";
import { Icon } from "../assets/icons";
import Button from "../components/ui/button";
import Input from "../components/ui/input";
import { getSites } from "../redux/api/siteAPI";
import { setUserSite } from "../redux/actions/authActions";
import LogoutModal from "../components/modals/user/LogoutModal";
import {
  bypassSiteSelection,
  completeSiteSelection,
} from "../utils/siteSelection";

export default function SiteSelector() {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const isAdmin    = useSelector((state) => state.auth?.isAdmin);
  const userData   = useSelector((state) => state.auth?.userData);

  const [sites,        setSites]        = useState([]);
  const [search,       setSearch]       = useState("");
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [selectedSite, setSelectedSite] = useState(null);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadSites = async () => {
      const { error: requestError, data } = await getSites(undefined, "asc", "Name", 1, 1000);
      if (cancelled) return;
      if (requestError) {
        setError("Sites could not be loaded. Try again.");
      } else {
        const availableSites = data?.sites || [];
        setSites(availableSites);
        if (availableSites.length === 1) setSelectedSite(availableSites[0]);
      }
      setLoading(false);
    };
    loadSites();
    return () => { cancelled = true; };
  }, []);

  const filteredSites = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return sites;
    return sites.filter((site) =>
      [site.Name, site.location, site.Description]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(query))
    );
  }, [search, sites]);

  useEffect(() => {
    if (selectedSite && !filteredSites.some((s) => s.Site_ID === selectedSite.Site_ID)) {
      setSelectedSite(null);
    }
  }, [filteredSites, selectedSite]);

  const selectSite = (site) => {
    const activeSite = completeSiteSelection(site);
    dispatch(setUserSite(activeSite));
    navigate("/", { replace: true });
  };

  const skipSiteSelection = () => {
    bypassSiteSelection();
    navigate("/", { replace: true });
  };

  const openSiteSetup = () => {
    bypassSiteSelection();
    navigate("/sites", { replace: true });
  };

  const handleLogout = () => {
    setIsLogoutModalOpen(true);
  };

  return (
    <main className="min-h-screen bg-[#F4F6FA] font-['Product Sans'] text-[#343b4d]">
      {isLogoutModalOpen && <LogoutModal isOpen={isLogoutModalOpen} onClose={() => setIsLogoutModalOpen(false)} />}

      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <div className="border-b border-gray-200 bg-white shadow-sm">
        <header className="mx-auto flex w-full max-w-5xl items-center justify-between px-5 py-4 sm:px-8">
          {/* Logo + title */}
          <div className="flex min-w-0 items-center gap-3">
            <img src={Logo} alt="Solyntek" className="h-10 w-auto flex-none" />
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#EF8400]">
                Solyntek workspace
              </p>
              <h1 className="mt-0.5 text-lg font-bold text-gray-800 sm:text-xl">
                Choose your site
              </h1>
            </div>
          </div>

          {/* User info + logout */}
          <div className="flex items-center gap-3">
            {userData && (
              <div className="hidden text-right sm:block">
                <p className="text-sm font-semibold text-gray-700">
                  {userData.First_name} {userData.Last_name}
                </p>
                <p className="text-xs text-gray-400">{userData.Email}</p>
              </div>
            )}
            <button
              type="button"
              onClick={handleLogout}
              title="Log out"
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 transition"
            >
              {/* <Icon icon="Logout" className="h-4 w-4" /> */}
              <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </header>
      </div>

      {/* ── Page body ───────────────────────────────────────────────────────── */}
      <section className="mx-auto w-full max-w-5xl px-5 py-8 sm:px-8">

        {/* Sub-header: title + search */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h2 className="text-base font-bold text-gray-800">Operational sites</h2>
            <p className="mt-0.5 text-sm text-gray-400">
              Select the location you want to monitor and manage.
            </p>
          </div>
          <div className="w-full md:w-72 rounded-xl border border-gray-300 bg-white">
            <Input
              icon="Search"
              placeholder="Search sites…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full"
            />
          </div>
        </div>

        {/* ── Loading skeletons ─────────────────────────────────────────────── */}
        {loading && (
          <div className="grid gap-4 md:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-36 animate-pulse rounded-xl border border-gray-100 bg-white" />
            ))}
          </div>
        )}

        {/* ── Error ────────────────────────────────────────────────────────── */}
        {!loading && error && (
          <div className="rounded-xl border border-[#efb6b1] bg-[#fdeaea] p-5 text-sm text-[#b5473f]">
            {error}
          </div>
        )}

        {/* ── Site grid ────────────────────────────────────────────────────── */}
        {!loading && !error && filteredSites.length > 0 && (
          <div className="grid gap-3 md:grid-cols-2">
            {filteredSites.map((site) => {
              const active = selectedSite?.Site_ID === site.Site_ID;
              return (
                <button
                  key={site.Site_ID}
                  type="button"
                  onClick={() => setSelectedSite(active ? null : site)}
                  aria-pressed={active}
                  className={`
                    group relative overflow-hidden rounded-xl border bg-white p-4 text-left
                    transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#0071BC]/30
                    ${active
                      ? "border-[#0071BC] shadow-md"
                      : "border-gray-100 shadow-sm hover:border-gray-300 hover:shadow-md"
                    }
                  `}
                >
                  {/* Left accent bar */}
                  <span className={`absolute inset-y-0 left-0 w-1 rounded-l-xl transition-colors ${active ? "bg-[#EF8400]" : "bg-transparent"}`} />

                  <div className="flex min-w-0 items-start gap-3 pl-2">
                    {/* Icon bubble */}
                    <span className={`
                      flex h-10 w-10 flex-none items-center justify-center rounded-lg transition-colors
                      ${active ? "bg-[#E5F2FA] text-[#0071BC]" : "bg-[#F4F6FA] text-gray-400"}
                    `}>
                      <Icon icon="SitePin" className="h-5 w-5" />
                    </span>

                    {/* Content */}
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className={`line-clamp-1 text-sm font-bold ${active ? "text-[#0071BC]" : "text-gray-800 group-hover:text-[#0071BC]"} transition-colors`}>
                          {site.Name}
                        </span>
                        {/* Radio indicator */}
                        <span className={`
                          flex h-4 w-4 flex-none items-center justify-center rounded-full border-2 transition-colors shrink-0
                          ${active ? "border-[#0071BC]" : "border-gray-300"}
                        `}>
                          {active && <span className="h-2 w-2 rounded-full bg-[#0071BC]" />}
                        </span>
                      </span>

                      <span className="mt-1.5 flex items-start gap-1.5 text-xs text-gray-400">
                        <Icon icon="MapPin" className="mt-0.5 h-3.5 w-3.5 flex-none" />
                        <span className="line-clamp-2 break-words">
                          {site.location || site.Description || "Location not configured"}
                        </span>
                      </span>

                      {(site.Work_hours || site.nbr_workers != null) && (
                        <span className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-gray-400">
                          {site.Work_hours  && <span>Hours: {site.Work_hours}</span>}
                          {site.nbr_workers != null && <span>{site.nbr_workers} workers</span>}
                        </span>
                      )}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* ── Empty state ───────────────────────────────────────────────────── */}
        {!loading && !error && filteredSites.length === 0 && (
          <div className="rounded-xl border border-gray-100 bg-white p-10 text-center shadow-sm">
            <Icon icon="MapPin" className="mx-auto h-8 w-8 text-gray-300" />
            <h2 className="mt-4 text-base font-bold text-gray-700">
              {sites.length ? "No matching sites" : "No sites available"}
            </h2>
            <p className="mt-1.5 text-sm text-gray-400">
              {sites.length
                ? "Try a different search term."
                : "Your account does not have access to any site yet."}
            </p>
            {!sites.length && isAdmin && (
              <div className="mt-5 flex justify-center">
                <Button color="secondary" size="large" onClick={openSiteSetup}>
                  Open site setup
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── Action footer ─────────────────────────────────────────────────── */}
        {!loading && !error && (
          <div className="mt-6 flex flex-col gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
            {/* Selection status / site count */}
            <p className="min-w-0 text-sm text-gray-400 font-['Product Sans']">
              {selectedSite ? (
                <>
                  Selected:{" "}
                  <strong className="font-semibold text-gray-700">
                    {selectedSite.Name}
                  </strong>
                </>
              ) : (
                <>
                  <span className="font-semibold text-gray-600">{sites.length}</span>{" "}
                  {sites.length === 1 ? "site" : "sites"} available
                </>
              )}
            </p>

            <div className="flex items-center gap-3">
              {/* Skip — always available */}
              <Button
                type="button"
                color="cancel"
                size="large"
                onClick={skipSiteSelection}
              >
                Skip for now
              </Button>

              {/* Confirm — only active when a site is selected */}
              <Button
                type="button"
                color="secondary"
                size="large"
                disabled={!selectedSite}
                onClick={() => selectedSite && selectSite(selectedSite)}
              >
                Open selected site
              </Button>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}